import { useSeo } from '../lib/seo';
/**
 * Static legal pages — Privacy, Cookies, Terms.
 * Content is stored as constants here so it's version-controlled with the codebase.
 * Update LAST_REVIEWED whenever copy changes.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const LAST_REVIEWED = 'February 2026';
const SITE = 'GoLeicestershire';
const CONTACT_EMAIL = 'info@goleicestershire.com';
const ADDRESS = '2 High Street, Loughborough, Leicestershire LE11 2PY';

function Wrapper({ title, slug, children }) {
  return (
    <div className="max-w-[860px] mx-auto px-6 lg:px-8 py-16 lg:py-24" data-testid={`legal-${slug}`}>
      <Helmet>
        <title>{`${title} · ${SITE}`}</title>
        <meta name="description" content={`${title} for the GoLeicestershire destination website.`} />
        <meta name="robots" content="index,follow" />
      </Helmet>
      <Link to="/" className="text-sm text-heritage hover:text-charcoal mb-6 inline-block">← Back to home</Link>
      <div className="label text-heritage mb-3">Legal</div>
      <h1 className="font-display text-4xl lg:text-6xl leading-[0.95] mb-3">{title}</h1>
      <p className="text-muted mb-12 text-sm">Last reviewed {LAST_REVIEWED}</p>
      <article className="prose prose-lg max-w-none text-charcoal/85 leading-relaxed [&_h2]:font-display [&_h2]:text-2xl [&_h2]:mt-10 [&_h2]:mb-3 [&_h3]:font-semibold [&_h3]:text-lg [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_a]:text-heritage [&_a:hover]:underline">
        {children}
      </article>
      <div className="mt-16 pt-8 border-t border-charcoal/10 text-sm text-muted">
        Questions? Email us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-heritage hover:underline">{CONTACT_EMAIL}</a> or write to {ADDRESS}.
      </div>
    </div>
  );
}

export function PrivacyPage() {
  useSeo('Privacy Policy | Go Leicestershire', 'How Go Leicestershire collects, uses and protects your data.');
  return (
    <Wrapper title="Privacy & Data Protection" slug="privacy">
      <p>
        This privacy notice explains how {SITE} (operated from {ADDRESS}) collects and uses information
        about visitors to <strong>goleicestershire.com</strong> and the small number of subscribers who
        opt in to our weekly digest. We comply with the UK General Data Protection Regulation (UK GDPR)
        and the Data Protection Act 2018.
      </p>

      <h2>1. Who we are (the &ldquo;data controller&rdquo;)</h2>
      <p>{SITE}, {ADDRESS}. Contact: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</p>

      <h2>2. What we collect</h2>
      <h3>2.1 Newsletter signups (lawful basis: <em>consent</em>)</h3>
      <ul>
        <li>Email address (required)</li>
        <li>Optional name (used to personalise the weekly email)</li>
        <li>The page on our site that the signup form was submitted from (e.g. homepage, digest page)</li>
      </ul>
      <p>
        Signups are forwarded to our email provider (EmailOctopus). EmailOctopus stores subscriber data
        in the EU and is a UK GDPR-compliant processor — see <a href="https://emailoctopus.com/legal/privacy" target="_blank" rel="noreferrer">their privacy notice</a>.
      </p>

      <h3>2.2 Anonymous usage analytics (lawful basis: <em>legitimate interest</em>)</h3>
      <ul>
        <li>The path of pages visited (e.g. <code>/explore/belvoir-castle</code>)</li>
        <li>The event type (page view, signup, itinerary build, outbound click, search query, award vote)</li>
        <li>Where the visit came from (referrer domain only — never the full URL)</li>
        <li>UTC timestamp</li>
      </ul>
      <p>
        We <strong>do not</strong> store IP addresses, device fingerprints, location data, names or any other
        personally identifying information in the analytics record. The tracker cannot identify any
        individual visitor.
      </p>

      <h3>2.3 Award nominations &amp; votes</h3>
      <p>
        Nominators provide a name and email so we can verify entries. One vote is allowed per browser
        per category — enforced by an anonymous token stored only on your device. We do not link votes
        to identities.
      </p>

      <h2>3. What we do NOT collect</h2>
      <ul>
        <li>No cookies are set by us for tracking, advertising, or profiling.</li>
        <li>No IP addresses or device IDs.</li>
        <li>No advertising / retargeting pixels (Facebook, Google Ads, etc.).</li>
        <li>We do not sell, rent, or share visitor data with third parties.</li>
      </ul>

      <h2>4. How long we keep it</h2>
      <ul>
        <li><strong>Newsletter subscribers:</strong> Until you unsubscribe (one click from any digest). Deleted within 30 days of unsubscribe.</li>
        <li><strong>Analytics events:</strong> 24 months rolling, then deleted automatically.</li>
        <li><strong>Award entries:</strong> For the duration of the cycle plus 12 months for historical archive (winners only).</li>
      </ul>

      <h2>5. Your rights</h2>
      <p>Under UK GDPR you have the right to:</p>
      <ul>
        <li>Access the personal data we hold about you (subject access request)</li>
        <li>Correct anything that is inaccurate</li>
        <li>Have your data erased (&ldquo;right to be forgotten&rdquo;)</li>
        <li>Restrict or object to our processing</li>
        <li>Withdraw consent at any time (for the newsletter — by unsubscribing)</li>
        <li>Lodge a complaint with the UK Information Commissioner&apos;s Office at <a href="https://ico.org.uk" target="_blank" rel="noreferrer">ico.org.uk</a></li>
      </ul>
      <p>To exercise any of these rights, email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We aim to respond within 14 days.</p>

      <h2>6. Children</h2>
      <p>This site is intended for adults planning trips with their families. We do not knowingly collect data from anyone under 13.</p>

      <h2>7. Changes to this notice</h2>
      <p>We review this privacy notice at least annually. The &ldquo;Last reviewed&rdquo; date at the top of the page shows when the current version came into effect.</p>
    </Wrapper>
  );
}

export function CookiesPage() {
  useSeo('Cookie Policy | Go Leicestershire', 'The cookies we use on Go Leicestershire and how to control them.');
  return (
    <Wrapper title="Cookies" slug="cookies">
      <p>
        {SITE} does <strong>not</strong> use cookies to track, profile, or advertise to its visitors. This
        page explains what little browser storage we do use, and why.
      </p>

      <h2>Strictly necessary — no consent required</h2>
      <ul>
        <li><strong>Anonymous vote token</strong> (<code>gl_voter_token</code>) — stored in <code>localStorage</code> on award voting pages. Lets us prevent the same browser voting twice in the same category. Cannot identify you. Lasts until you clear browser data.</li>
        <li><strong>Admin session token</strong> (<code>gl_admin_token</code>) — only stored if you log in to the editorial dashboard. Not used by public visitors.</li>
      </ul>

      <h2>What we don&apos;t use</h2>
      <ul>
        <li>No advertising or retargeting cookies</li>
        <li>No third-party analytics cookies (Google Analytics, Facebook Pixel, etc.)</li>
        <li>No social media trackers</li>
        <li>No A/B testing cookies</li>
      </ul>

      <h2>Our analytics</h2>
      <p>
        Our in-house analytics tracker records anonymous usage events (page views, signups, clicks) but
        works entirely without cookies. See our <Link to="/privacy">Privacy notice</Link> for the full list.
      </p>

      <h2>How to clear local storage</h2>
      <p>
        You can clear all browser storage from this site at any time via your browser&apos;s settings (look
        for &ldquo;Clear site data&rdquo; or &ldquo;Cookies and site data&rdquo;).
      </p>
    </Wrapper>
  );
}

export function TermsPage() {
  useSeo('Terms of Use | Go Leicestershire', 'Terms and conditions for using the Go Leicestershire website.');
  return (
    <Wrapper title="Terms of Use" slug="terms">
      <p>
        By accessing <strong>goleicestershire.com</strong> you agree to these terms. If you disagree with any
        part, please stop using the site.
      </p>

      <h2>1. Editorial purpose</h2>
      <p>
        {SITE} is an independent destination guide for Leicestershire. We list attractions, events, market
        towns, and accommodation in good faith. Inclusion of a venue or event on this site is not an
        endorsement, nor does it imply any commercial relationship unless explicitly marked as a paid
        partnership.
      </p>

      <h2>2. Copyright</h2>
      <p>
        All editorial content — text, page layouts, our logo, the &ldquo;goLeicestershire&rdquo; name, and our own
        photography — is © {new Date().getFullYear()} {SITE}, all rights reserved.
      </p>
      <p>
        Photographs supplied by venues or featured partners remain the property of their respective
        owners and are used here with permission. If you believe any image on this site infringes your
        copyright, please contact us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we will
        review and remove it within 5 working days where appropriate.
      </p>

      <h2>3. Third-party links &amp; bookings</h2>
      <p>
        Many pages link out to third-party websites (attraction sites, ticket sellers, TripAdvisor,
        StayPlayExplore, etc.). We do not control those sites and we are not responsible for their
        content, prices, availability, privacy practices or terms. Always check directly with the
        provider before relying on opening times, prices or booking conditions.
      </p>

      <h2>4. Event &amp; opening time accuracy</h2>
      <p>
        Event listings are largely populated automatically from public feeds (iCal, Eventbrite) and may
        contain errors or be cancelled at short notice by the organiser. Always confirm an event
        directly with the host before travelling.
      </p>

      <h2>5. User-submitted content</h2>
      <p>
        Anyone can nominate a venue for the GoLeicestershire Tourism Awards. By submitting a nomination
        you confirm the information is true to the best of your knowledge and grants {SITE} the right
        to display the nomination on the public ballot once approved. We reserve the right to reject any
        submission without explanation. Nominators&apos; email addresses are not published.
      </p>

      <h2>6. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Scrape, mirror or republish substantial portions of the site without written permission</li>
        <li>Submit fake nominations, spam votes, or attempt to manipulate award results</li>
        <li>Use automated software to interfere with the site&apos;s normal operation</li>
        <li>Use the contact email for unsolicited marketing or harassment</li>
      </ul>

      <h2>7. No warranty / limitation of liability</h2>
      <p>
        The site is provided &ldquo;as is.&rdquo; We make no warranty that it will be uninterrupted, error-free
        or that the content is accurate or complete. To the maximum extent permitted by law, {SITE} is
        not liable for any indirect or consequential loss arising from your use of the site or reliance on
        any information published on it.
      </p>

      <h2>8. Governing law</h2>
      <p>
        These terms are governed by the law of England and Wales and any dispute will be subject to the
        exclusive jurisdiction of the English courts.
      </p>

      <h2>9. Contact</h2>
      <p>
        Questions, complaints, or takedown requests:&nbsp;
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
      </p>
    </Wrapper>
  );
}
